'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { type FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconArrowRight, IconHelp } from '@redteclab/icons'

import { Notification } from 'base-ui'

import { ContactPageFlatCta } from '../../contact-page'
import {
  CONTACT_FORM_SUBJECT_KEY,
  DirectContactFormDialogWrapper,
  directContactFormSubjectCatalogGetValue,
} from '../../direct-contact'
import { type OpenableControls, useOpenableControls } from '../../generic-hooks'
import { useGlobalConfigContext } from '../../global-config'
import {
  urlResolverGetFaq,
  urlResolverGetOtherTopicsContactPage,
} from '../../url-handling'
import { AMAZONISATION_GENERAL_TRACKING_ID } from '../model/ORIGIN_PAGES'

import { OtherTopicsCallUsCta } from './OtherTopicsCallUsCta'
import { OtherTopicsContactFormPageCta } from './OtherTopicsContactFormPageCta'

const CONTACT_SUBJECT_QUERY_PARAM = 'contactSubject'
const CONTACT_SUBJECT_QUERY_VALUE_CANCEL_CONTRACT = 'cancel-contract'
const OPEN_CONTACT_FORM_QUERY_PARAM = 'openContactForm'

export const OtherTopicsContactPageOptions: FC = () => {
  const globalConfig = useGlobalConfigContext()
  const [isSuccess, setIsSuccess] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const intl = useIntl()
  const {
    handleClose: handleCloseModal,
    handleOpen: handleOpenModal,
    isOpen,
  } = useOpenableControls()

  const handleCloseContactForm = useCallback(() => {
    handleCloseModal()

    if (searchParams.has(OPEN_CONTACT_FORM_QUERY_PARAM)) {
      router.replace(urlResolverGetOtherTopicsContactPage(globalConfig))
    }
  }, [globalConfig, handleCloseModal, router, searchParams])

  const modalControlsFormDialog = useMemo<OpenableControls>(
    () => ({
      handleClose: handleCloseContactForm,
      handleOpen: handleOpenModal,
      handleToggle: isOpen ? handleCloseContactForm : handleOpenModal,
      isOpen,
    }),
    [handleCloseContactForm, handleOpenModal, isOpen],
  )

  const shouldOpenCancelContractForm =
    searchParams.get(CONTACT_SUBJECT_QUERY_PARAM) ===
    CONTACT_SUBJECT_QUERY_VALUE_CANCEL_CONTRACT
  const formSubject = shouldOpenCancelContractForm
    ? directContactFormSubjectCatalogGetValue(
        intl,
        CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT,
      )
    : undefined
  const contactFormLaunchOrigin = searchParams.get('source')
    ? AMAZONISATION_GENERAL_TRACKING_ID
    : undefined

  const onSuccess = (): void => {
    setIsSuccess(true)
  }

  return (
    <>
      {isSuccess ? (
        <Notification className="container my-8 mt-0 w-full" severity="success">
          <FormattedMessage id="product.consultation.message.success" />
        </Notification>
      ) : null}
      <div className="container mb-4 w-full pt-8">
        <div className="grid w-full grid-cols-1 gap-6 tablet:grid-cols-2 tablet:gap-x-4 tablet:gap-y-6">
          <OtherTopicsContactFormPageCta />
          <OtherTopicsCallUsCta />
          <ContactPageFlatCta<'a'>
            endIcon={<IconArrowRight className="fill-dark-brand" />}
            href={urlResolverGetFaq(globalConfig)}
            id="contact.call.scheduling.label.v2"
            startIcon={<IconHelp className="fill-dark-brand" />}
          >
            <span className="grow whitespace-normal text-start">
              <FormattedMessage id="help.centre.others.options.faq.page" />
            </span>
          </ContactPageFlatCta>
        </div>
      </div>
      <DirectContactFormDialogWrapper
        contactFormLaunchOrigin={contactFormLaunchOrigin}
        disableSubject={shouldOpenCancelContractForm}
        formSubject={formSubject}
        modalControls={modalControlsFormDialog}
        onSuccess={onSuccess}
      />
    </>
  )
}
